import { CellContext } from '@tanstack/react-table';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { fieldsConfig } from '@/components/table/_fieldDefinitions/fieldsConfig.ts';
import { CellChipList } from '@/components/table/_fieldDefinitions/_base/CellChipList.tsx';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';

export interface NestedObjectListCellContext {
  itemType: FieldType;
}

/**
 * Renders a list of nested objects as chips. Each chip's label is computed by
 * delegating to the per-item type's `exportRender` (e.g. `NESTED_INSURANCE`
 * returns `item.name`) — so adding a new `itemType` only requires a new export
 * renderer, not a change here.
 */
export const nestedObjectListFieldRender = <T,>(
  info: CellContext<T, unknown>,
  customFieldsContext?: NestedObjectListCellContext,
) => {
  const items = (info.getValue() as unknown[] | null | undefined) ?? [];
  if (items.length === 0) return <EmptyValue />;

  const itemType = customFieldsContext?.itemType;
  const itemExportRender = itemType
    ? fieldsConfig[itemType].exportRender
    : undefined;

  return (
    <CellChipList
      items={items.map((item, idx) => {
        const label = itemExportRender
          ? String(itemExportRender(item) ?? '')
          : String((item as { name?: string })?.name ?? '');
        return {
          key: (item as { id?: string })?.id ?? `${label}-${idx}`,
          label,
        };
      })}
    />
  );
};
